import React, { useState } from 'react';
import { Form, Input, Button, Card, Typography, Alert, Row, Col, Result } from 'antd';
import { UserOutlined, LockOutlined, MailOutlined, PhoneOutlined, IdcardOutlined } from '@ant-design/icons';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { authService, RegisterInput } from '../services/authService';

const { Title, Text } = Typography;

const Register: React.FC = () => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [registeredEmail, setRegisteredEmail] = useState<string | null>(null);
  const [resendMsg, setResendMsg] = useState<string | null>(null);
  const navigate = useNavigate();
  const auth = useAuth();
  const [searchParams] = useSearchParams();

  // Email may be pre-filled (e.g. from the post-guest-checkout prompt).
  const prefillEmail = searchParams.get('email') || '';

  const handleRegister = async (values: RegisterInput & { confirmPassword?: string }) => {
    setLoading(true);
    setError(null);
    try {
      const { auth: authData } = await authService.register({
        userId: values.userId,
        email: values.email,
        name: values.name,
        password: values.password,
        handPhone: (values.handPhone || '').trim(),
      });
      // Auto-login (account is active immediately; email still unverified).
      await auth.login(authData);
      setRegisteredEmail(authData.user.email);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    if (!registeredEmail) return;
    setResendMsg(null);
    try {
      await authService.resendVerification(registeredEmail);
      setResendMsg('Verification email sent again — please check your inbox.');
    } catch {
      setResendMsg('Could not resend right now. Please try again shortly.');
    }
  };

  // Success screen after registration.
  if (registeredEmail) {
    return (
      <div style={{ minHeight: '100vh', background: '#f0f2f5', padding: '40px 0' }}>
        <Row justify="center" align="middle">
          <Col xs={22} sm={18} md={14} lg={10} xl={8}>
            <Card>
              <Result
                status="success"
                title="Account created!"
                subTitle={
                  <span>
                    We've sent a verification link to <strong>{registeredEmail}</strong>. Verify your
                    email to secure your account and automatically add any orders you placed as a guest.
                  </span>
                }
                extra={[
                  <Button type="primary" key="shop" onClick={() => navigate('/products')}>
                    Start Shopping
                  </Button>,
                  <Button key="orders" onClick={() => navigate('/profile?tab=orders')}>
                    My Orders
                  </Button>,
                  <Button type="link" key="resend" onClick={handleResend}>
                    Resend email
                  </Button>,
                ]}
              />
              {resendMsg && <Alert type="info" showIcon message={resendMsg} style={{ marginTop: 8 }} />}
            </Card>
          </Col>
        </Row>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', background: '#f0f2f5', padding: '40px 0' }}>
      <Row justify="center" align="middle" style={{ minHeight: '60vh' }}>
        <Col xs={22} sm={16} md={12} lg={8} xl={7}>
          <Card>
            <div style={{ textAlign: 'center', marginBottom: 24 }}>
              <Title level={2}>Create Account</Title>
              <Text type="secondary">Sign up to track your orders with Dasoon Eggs</Text>
            </div>

            {error && (
              <Alert
                message={error}
                type="error"
                showIcon
                closable
                onClose={() => setError(null)}
                style={{ marginBottom: 16 }}
              />
            )}

            <Form
              form={form}
              name="register"
              onFinish={handleRegister}
              layout="vertical"
              size="large"
              initialValues={{ email: prefillEmail }}
            >
              {/* Sign In asks for "User ID or Email", so registration has to
                  offer an ID to type there (issue #102). */}
              <Form.Item
                name="userId"
                label="User ID"
                rules={[
                  { required: true, message: 'Please choose a User ID' },
                  { min: 4, max: 50, message: 'User ID must be between 4 and 50 characters' },
                  {
                    pattern: /^[A-Za-z0-9][A-Za-z0-9._-]*$/,
                    message:
                      'Use letters, numbers, dots, hyphens or underscores, starting with a letter or number',
                  },
                ]}
                tooltip="Choose an ID to sign in with. You can sign in with your email instead."
              >
                <Input prefix={<IdcardOutlined />} placeholder="Choose a User ID" autoComplete="username" />
              </Form.Item>

              <Form.Item
                name="email"
                label="Email"
                rules={[
                  { required: true, message: 'Please enter your email' },
                  { type: 'email', message: 'Please enter a valid email' },
                  { max: 50, message: 'Email must be 50 characters or less' },
                ]}
                tooltip="You can sign in with your email as well as your User ID."
              >
                <Input prefix={<MailOutlined />} placeholder="you@example.com" autoComplete="email" />
              </Form.Item>

              <Form.Item
                name="name"
                label="Name"
                rules={[
                  { required: true, message: 'Please enter your name' },
                  { max: 100, message: 'Name must be 100 characters or less' },
                ]}
              >
                <Input prefix={<UserOutlined />} placeholder="Your name" autoComplete="name" />
              </Form.Item>

              <Form.Item
                name="handPhone"
                label="Phone"
                rules={[
                  { required: true, whitespace: true, message: 'Please enter your phone number' },
                  { max: 20, message: 'Phone must be 20 characters or less' },
                ]}
                tooltip="Used to send you delivery photos over WhatsApp."
              >
                <Input prefix={<PhoneOutlined />} placeholder="Contact number" autoComplete="tel" />
              </Form.Item>

              <Form.Item
                name="password"
                label="Password"
                rules={[
                  { required: true, message: 'Please enter a password' },
                  { min: 6, message: 'Password must be at least 6 characters' },
                  { max: 20, message: 'Password must be 20 characters or less' },
                ]}
                tooltip="6–20 characters."
              >
                <Input.Password prefix={<LockOutlined />} placeholder="Create a password" autoComplete="new-password" />
              </Form.Item>

              <Form.Item
                name="confirmPassword"
                label="Confirm Password"
                dependencies={['password']}
                rules={[
                  { required: true, message: 'Please confirm your password' },
                  ({ getFieldValue }) => ({
                    validator(_, value) {
                      if (!value || getFieldValue('password') === value) return Promise.resolve();
                      return Promise.reject(new Error('Passwords do not match'));
                    },
                  }),
                ]}
              >
                <Input.Password prefix={<LockOutlined />} placeholder="Re-enter your password" autoComplete="new-password" />
              </Form.Item>

              <Form.Item style={{ marginBottom: 8 }}>
                <Button type="primary" htmlType="submit" loading={loading} block size="large">
                  Create Account
                </Button>
              </Form.Item>
            </Form>

            <div style={{ textAlign: 'center', marginTop: 16 }}>
              <Text type="secondary">Already have an account? </Text>
              <Button type="link" size="small" onClick={() => navigate('/login')} style={{ padding: 0 }}>
                Sign In
              </Button>
            </div>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default Register;
